import { useState, useEffect, useRef } from "react";
import SingleTeam from "../../components/Team";
import MemberImage from "../../assets/img/members/member.jpg";
import { founder_members } from "../../utils";

// Simplified Field of Work Dropdown - No search input, auto-call on select
const FieldOfWorkDropdown = ({ value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Field of work options
  const fieldOfWorkOptions = [
    { label: "Hatchery Sector", value: "Hatchery Sector" },
    { label: "Farm Sector", value: "Farm Sector" },
    { label: "Healthcare Sector", value: "Healthcare Sector" },
    { label: "Feed Sector", value: "Feed Sector" },
    { label: "Machinery Sector", value: "Machinery Sector" },
    { label: "Government Sector", value: "Government Sector" },
    { label: "Processing Sector", value: "Processing Sector" },
    {
      label: "Fish Farming & Allied Sector",
      value: "Fish Farming & Allied Sector",
    },
    {
      label: "Markets, Certifications, Policy",
      value: "Markets, Certifications, Policy",
    },
    { label: "Media", value: "Media" },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (selectedValue) => {
    onChange({ target: { value: selectedValue } });
    setIsOpen(false);
  };

  const selectedOption = fieldOfWorkOptions.find(
    (option) => option.value === value
  );

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full h-12 sm:h-14 px-4 sm:px-6 pr-10 sm:pr-12 border-2 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 shadow-sm text-left ${
          isOpen
            ? "border-blue-500 bg-white ring-4 ring-blue-100"
            : "border-gray-200 bg-gray-50 hover:bg-white"
        }`}
      >
        <span className={selectedOption ? "text-gray-900" : "text-gray-500"}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <div className="absolute inset-y-0 right-3 sm:right-4 flex items-center">
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-xl max-h-60 overflow-hidden">
          <div className="max-h-60 overflow-y-auto">
            {fieldOfWorkOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={`w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors duration-150 text-sm ${
                  value === option.value
                    ? "bg-blue-100 text-blue-900 font-medium"
                    : "text-gray-900"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MembersSection = ({
  members = [],
  page,
  hasMore,
  onNext,
  onPrev,
  setSearchParams,
  loading,
  designations = [],
  currentOfficeBearerIds = [],
  setPage,
  searchType,
  setSearchType,
  membersSectionRef,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [alphabetFilter, setAlphabetFilter] = useState("");
  const [filters, setFilters] = useState({
    name: "",
    membershipId: "",
    fieldOfWork: "",
    designation: "",
    currentOfficeBearers: "",
    memberRole: "",
    alphabetFilter: "",
    pastPresident: "", // Added pastPresident filter
  });

  const [activeRole, setActiveRole] = useState("");
  const [hasInitialized, setHasInitialized] = useState(false);

  const searchTypes = [
    { value: "name", label: "By Name", placeholder: "Enter member name..." },
    {
      value: "membershipId",
      label: "By Membership No",
      placeholder: "Enter membership number...",
    },
    {
      value: "fieldOfWork",
      label: "By Field of Work",
      placeholder: "Enter field of work...",
    },
  ];

  const shouldShowAlphabetFilter = () => {
    return (
      filters.designation === "6896f0a379b374d7da63d536" &&
      activeRole &&
      activeRole !== "Current Office Bearers" &&
      activeRole !== "Founder Member" &&
      activeRole !== "Past President"
    );
  };

  useEffect(() => {
    if (hasInitialized) return;

    const urlParams = new URLSearchParams(window.location.search);
    let foundUrlParams = false;
    const nameValue = urlParams.get("name");
    const isFieldWork = urlParams.get("isFieldWork") === "true";

    if (nameValue) {
      const match = founder_members.find(
        (f) =>
          f.name.toLowerCase() === decodeURIComponent(nameValue).toLowerCase()
      );
      if (match) {
        const updatedFilters = {
          name: decodeURIComponent(nameValue),
          membershipId: "",
          fieldOfWork: "",
          designation: "",
          currentOfficeBearers: "",
          memberRole: "Founder Member",
          alphabetFilter: "",
          pastPresident: "",
        };
        setFilters(updatedFilters);
        setSearchParams(updatedFilters);
        setActiveRole("Founder Member");
        setSearchType("name");
        setSearchInput(decodeURIComponent(nameValue));
        setHasInitialized(true);
        return;
      }
    }

    if (isFieldWork) {
      const fieldOfWorkValue = urlParams.get("fieldOfWork");
      if (fieldOfWorkValue) {
        foundUrlParams = true;
        setSearchType("fieldOfWork");
        setSearchInput(decodeURIComponent(fieldOfWorkValue));
        const updatedFilters = {
          name: "",
          membershipId: "",
          fieldOfWork: decodeURIComponent(fieldOfWorkValue),
          designation: "",
          currentOfficeBearers: "",
          memberRole: "",
          alphabetFilter: "",
          pastPresident: "",
        };
        setFilters(updatedFilters);
        setSearchParams(updatedFilters);
        setActiveRole("");
        setHasInitialized(true);
        return;
      }
    }

    const searchParamsToCheck = ["name", "membershipId"];
    for (const param of searchParamsToCheck) {
      const value = urlParams.get(param);
      if (value && !isFieldWork) {
        foundUrlParams = true;
        setSearchType(param);
        setSearchInput(decodeURIComponent(value));
        const updatedFilters = {
          name: "",
          membershipId: "",
          fieldOfWork: "",
          designation: "",
          currentOfficeBearers: "",
          memberRole: "",
          alphabetFilter: "",
          pastPresident: "",
        };
        updatedFilters[param] = decodeURIComponent(value);
        setFilters(updatedFilters);
        setSearchParams(updatedFilters);
        setActiveRole("");
        setHasInitialized(true);
        return;
      }
    }

    if (!foundUrlParams) {
      const defaultFilters = {
        name: "",
        membershipId: "",
        fieldOfWork: "",
        designation: "",
        currentOfficeBearers: currentOfficeBearerIds.join(","),
        memberRole: "",
        alphabetFilter: "",
        pastPresident: "",
      };
      setFilters(defaultFilters);
      setSearchParams(defaultFilters);
      setActiveRole("Current Office Bearers");
    }

    setHasInitialized(true);
  }, [
    hasInitialized,
    currentOfficeBearerIds,
    setSearchParams,
    setSearchType,
    founder_members,
  ]);

  const handleSearch = () => {
    const resetFilters = {
      name: "",
      membershipId: "",
      fieldOfWork: "",
      designation: "",
      currentOfficeBearers: "",
      memberRole: "",
      alphabetFilter: "",
      pastPresident: "",
    };

    if (searchInput?.trim()) {
      const match = founder_members.find(
        (f) => f?.name.toLowerCase() === searchInput?.trim().toLowerCase()
      );
      if (match) {
        resetFilters.name = searchInput.trim();
        resetFilters.memberRole = "Founder Member";
        setActiveRole("Founder Member");
      } else {
        resetFilters[searchType] = searchInput.trim();
        setActiveRole("");
      }

      const urlParams = new URLSearchParams();
      urlParams.set(searchType, encodeURIComponent(searchInput.trim()));
      if (searchType === "fieldOfWork") {
        urlParams.set("isFieldWork", "true");
      }
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${urlParams.toString()}`
      );
    } else {
      setActiveRole("Current Office Bearers");
      resetFilters.currentOfficeBearers = currentOfficeBearerIds.join(",");
      window.history.replaceState({}, "", window.location.pathname);
    }

    if (!searchInput.trim()) {
      if (filters.designation) resetFilters.designation = filters.designation;
      if (filters.currentOfficeBearers)
        resetFilters.currentOfficeBearers = filters.currentOfficeBearers;
      if (filters.memberRole) resetFilters.memberRole = filters.memberRole;
      if (filters.alphabetFilter)
        resetFilters.alphabetFilter = filters.alphabetFilter;
      if (filters.pastPresident)
        resetFilters.pastPresident = filters.pastPresident;
    }
    setFilters(resetFilters);
    setSearchParams(resetFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput("");
    setSearchType("name");
    setAlphabetFilter("");

    const resetFilters = {
      name: "",
      membershipId: "",
      fieldOfWork: "",
      designation: "",
      currentOfficeBearers: currentOfficeBearerIds.join(","),
      memberRole: "",
      alphabetFilter: "",
      pastPresident: "",
    };

    setFilters(resetFilters);
    setActiveRole("Current Office Bearers");
    setSearchParams(resetFilters);
    setPage(1);

    // Clear URL parameters
    window.history.replaceState({}, "", window.location.pathname);
  };

  const handleRoleFilter = (role) => {
    let newActive = activeRole === role ? "" : role;
    let updatedFilters = { ...filters };

    setSearchInput("");
    setSearchType("name");
    setAlphabetFilter("");

    if (role === "Current Office Bearers") {
      if (activeRole === role) return;
      updatedFilters = {
        name: "",
        membershipId: "",
        fieldOfWork: "",
        designation: "",
        currentOfficeBearers: newActive ? currentOfficeBearerIds.join(",") : "",
        memberRole: "",
        alphabetFilter: "",
        pastPresident: "",
      };
    } else if (role === "Founder Member") {
      updatedFilters = {
        name: "",
        membershipId: "",
        fieldOfWork: "",
        currentOfficeBearers: "",
        designation: "",
        memberRole: newActive ? "Founder Member" : "",
        alphabetFilter: "",
        pastPresident: "",
      };
    } else if (role === "Past President") {
      // Handle Past President with boolean value
      updatedFilters = {
        name: "",
        membershipId: "",
        fieldOfWork: "",
        currentOfficeBearers: "",
        designation: "",
        memberRole: "",
        alphabetFilter: "",
        pastPresident: newActive ? "true" : "",
      };
    } else {
      const roleData = designations.find((r) => r.name === role);
      updatedFilters = {
        name: "",
        membershipId: "",
        fieldOfWork: "",
        currentOfficeBearers: "",
        designation: roleData && newActive ? roleData._id : "",
        memberRole: "",
        alphabetFilter: "",
        pastPresident: "",
      };
    }

    if (!newActive) {
      newActive = "Current Office Bearers";
      updatedFilters = {
        name: "",
        membershipId: "",
        fieldOfWork: "",
        designation: "",
        currentOfficeBearers: currentOfficeBearerIds.join(","),
        memberRole: "",
        alphabetFilter: "",
        pastPresident: "",
      };
    }

    setActiveRole(newActive);
    setFilters(updatedFilters);
    setPage(1);
    setSearchParams(updatedFilters);

    window.history.replaceState({}, "", window.location.pathname);
  };

  const handleAlphabetFilter = (letter) => {
    const newAlphabetFilter = alphabetFilter === letter ? "" : letter;
    setAlphabetFilter(newAlphabetFilter);

    const updatedFilters = {
      ...filters,
      alphabetFilter: newAlphabetFilter,
    };

    setFilters(updatedFilters);
    setSearchParams(updatedFilters);
    setPage(1);
  };

  const preserveScroll = (fn) => {
    const y = window.scrollY;
    fn();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: y, behavior: "auto" });
      });
    });
  };

  const handleSearchTypeChange = (newType) => {
    preserveScroll(() => {
      setSearchType(newType);
      setSearchInput("");
      setActiveRole("");
    });
  };

  const getCurrentPlaceholder = () => {
    const currentType = searchTypes.find((type) => type.value === searchType);
    return currentType ? currentType.placeholder : "Enter search term...";
  };

  const removeFilter = (filterKey) => {
    const updatedFilters = { ...filters };
    updatedFilters[filterKey] = "";

    if (filterKey === "alphabetFilter") {
      setAlphabetFilter("");
    }
    if (
      filterKey === "designation" ||
      filterKey === "currentOfficeBearers" ||
      filterKey === "memberRole" ||
      filterKey === "pastPresident"
    ) {
      setActiveRole("Current Office Bearers");
      updatedFilters.currentOfficeBearers = currentOfficeBearerIds.join(",");
      updatedFilters.designation = "";
      updatedFilters.memberRole = "";
      updatedFilters.pastPresident = "";
      updatedFilters.alphabetFilter = "";
      setAlphabetFilter("");
    } else {
      if (
        !activeRole &&
        !updatedFilters.designation &&
        !updatedFilters.currentOfficeBearers &&
        !updatedFilters.memberRole &&
        !updatedFilters.pastPresident
      ) {
        setActiveRole("Current Office Bearers");
        updatedFilters.currentOfficeBearers = currentOfficeBearerIds.join(",");
      }
    }

    if (filterKey === searchType) {
      setSearchInput("");
    }

    // Update URL parameters
    const urlParams = new URLSearchParams(window.location.search);

    if (filterKey === "fieldOfWork") {
      urlParams.delete("fieldOfWork");
      urlParams.delete("isFieldWork");
    } else {
      urlParams.delete(filterKey);
    }

    // Check if any search filters remain active
    const searchFilterKeys = ["name", "membershipId", "fieldOfWork"];
    const hasActiveSearchFilters = searchFilterKeys.some(
      (key) => updatedFilters[key] && updatedFilters[key] !== ""
    );

    // If no active search filters remain, clear the URL or set default state
    if (!hasActiveSearchFilters) {
      window.history.replaceState({}, "", window.location.pathname);
    } else {
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${urlParams.toString()}`
      );
    }

    setFilters(updatedFilters);
    setSearchParams(updatedFilters);
    setPage(1);
  };

  const getActiveFilters = () => {
    const activeFilters = [];

    Object.entries(filters).forEach(([key, value]) => {
      if (
        value &&
        key !== "designation" &&
        key !== "currentOfficeBearers" &&
        key !== "memberRole" &&
        key !== "alphabetFilter" &&
        key !== "pastPresident"
      ) {
        const searchTypeObj = searchTypes.find((type) => type.value === key);
        activeFilters.push({
          key,
          label: searchTypeObj ? searchTypeObj.label.replace("By ", "") : key,
          value: value,
          type: "search",
        });
      }
    });

    if (filters.alphabetFilter) {
      activeFilters.push({
        key: "alphabetFilter",
        label: "First Name Starts With",
        value: filters.alphabetFilter,
        type: "alphabet",
      });
    }

    if (filters.currentOfficeBearers) {
      activeFilters.push({
        key: "currentOfficeBearers",
        label: "Role",
        value: "Current Office Bearers",
        type: "role",
      });
    } else if (filters.memberRole) {
      activeFilters.push({
        key: "memberRole",
        label: "Role",
        value: filters.memberRole,
        type: "role",
      });
    } else if (filters.pastPresident === "true") {
      activeFilters.push({
        key: "pastPresident",
        label: "Role",
        value: "Past President",
        type: "role",
      });
    } else if (filters.designation) {
      const roleData = designations.find(
        (role) => role.id === filters.designation
      );
      if (roleData) {
        activeFilters.push({
          key: "designation",
          label: "Role",
          value: roleData.name,
          type: "role",
        });
      }
    }

    return activeFilters;
  };

  const activeFilters = getActiveFilters();
  const hasActiveFilters = activeFilters.length > 0;

  // const filteredMembers = members.filter((member) => {
  //   const first = member.profileInfo?.firstName?.trim();
  //   const last = member.profileInfo?.lastName?.trim();
  //   if (first && last && first.toLowerCase() === last.toLowerCase()) {
  //     return false;
  //   }
  //   return true;
  // });

  // Check if we're showing founder members
  const isFounderMemberView = activeRole === "Founder Member";

  let pageSize;

  if (activeRole === "Founder Member") {
    pageSize = 100;
  } else {
    pageSize = 20;
  }
  let displayMembers = members;
  let localHasMore = hasMore;

  if (isFounderMemberView) {
    let filteredFounders = [...founder_members];

    if (filters.name) {
      const searchTerm = filters.name.toLowerCase();
      filteredFounders = filteredFounders.filter((f) =>
        f.name.toLowerCase().includes(searchTerm)
      );
    }

    // Apply alphabet filter
    if (filters.alphabetFilter) {
      const letter = filters.alphabetFilter.toUpperCase();
      filteredFounders = filteredFounders.filter((f) =>
        f.name.toUpperCase().startsWith(letter)
      );
    }

    // Sort by sno
    filteredFounders = filteredFounders.sort((a, b) => a.sno - b.sno);

    // Local pagination
    const total = filteredFounders.length;
    const totalPages = Math.ceil(total / pageSize);
    const start = (page - 1) * pageSize;
    const end = Math.min(start + pageSize, total);
    displayMembers = filteredFounders.slice(start, end);
    localHasMore = end < total;
  }

  // Wrappers for next/prev to handle local for founder
  const handlePrev = () => {
    if (isFounderMemberView) {
      if (page > 1) {
        preserveScroll(() => setPage(page - 1));
      }
    } else {
      onPrev();
    }
  };

  const handleNext = () => {
    if (isFounderMemberView) {
      if (localHasMore) {
        preserveScroll(() => setPage(page + 1));
      }
    } else {
      onNext();
    }
  };

  const effectiveHasMore = isFounderMemberView ? localHasMore : hasMore;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Member Directory
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Search and explore our community of professionals
          </p>
        </div>
        {/* Search Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 mb-8">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">
              Search Members
            </h2>
            {/* Search Type Radio Buttons */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-4">
                Search Type
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {searchTypes.map((type) => (
                  <label
                    key={type.value}
                    className={`flex items-center p-3 border-2 rounded-xl cursor-pointer transition-all duration-300 ${
                      searchType === type.value
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="searchType"
                      value={type.value}
                      checked={searchType === type.value}
                      onChange={(e) => handleSearchTypeChange(e.target.value)}
                      className="!sr-only"
                    />
                    <div
                      className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                        searchType === type.value
                          ? "border-blue-500 bg-blue-500"
                          : "border-gray-300"
                      }`}
                    >
                      {searchType === type.value && (
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                      )}
                    </div>
                    <span className="text-sm font-medium">{type.label}</span>
                  </label>
                ))}
              </div>
            </div>
            {/* Search Input and Buttons */}
            <div className="flex flex-col lg:flex-row gap-6 lg:items-end">
              <div className="w-full lg:flex-grow ">
                <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                  Search Value
                </label>

                {/* Show dropdown for fieldOfWork with auto-search */}
                {searchType === "fieldOfWork" ? (
                  <FieldOfWorkDropdown
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Select field of work..."
                  />
                ) : (
                  <div className="relative w-full">
                    <input
                      type="text"
                      placeholder={getCurrentPlaceholder()}
                      className="w-full h-12 sm:h-14 px-4 sm:px-6 pr-10 sm:pr-12 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 bg-gray-50 focus:bg-white shadow-sm"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                    />
                    <div className="absolute inset-y-0 right-3 sm:right-4 flex items-center">
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                  </div>
                )}
              </div>

              {/* Search and Reset buttons */}
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <button
                  onClick={handleSearch}
                  disabled={
                    !searchInput.trim() &&
                    !filters.designation &&
                    !filters.currentOfficeBearers &&
                    !filters.memberRole &&
                    !filters.pastPresident
                  }
                  className={`h-12 sm:h-14 w-full sm:w-auto px-6 sm:px-8 font-semibold rounded-xl transition-all duration-300 ${
                    !searchInput.trim() &&
                    !filters.designation &&
                    !filters.currentOfficeBearers &&
                    !filters.memberRole &&
                    !filters.pastPresident
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700"
                  }`}
                >
                  Search
                </button>
                <button
                  onClick={handleResetFilters}
                  className="h-12 sm:h-14 w-full sm:w-auto px-6 sm:px-8 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all duration-300"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Active Filters Display */}
        {hasActiveFilters && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-8">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center">
                  <svg
                    className="w-5 h-5 mr-2 text-blue-600 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z"
                    />
                  </svg>
                  Active Filters ({activeFilters.length})
                </h3>
                <button
                  onClick={handleResetFilters}
                  className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center transition-colors duration-200"
                >
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  Clear All
                </button>
              </div>
            </div>
            {/* Filters */}
            <div className="p-4 sm:p-6">
              <div className="space-y-4 sm:space-y-0 sm:flex sm:flex-wrap sm:gap-4">
                {activeFilters.map((filter, index) => (
                  <div
                    key={`${filter.key}-${index}`}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full sm:w-auto p-1 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl border border-gray-200 hover:shadow-md transition-all duration-200"
                  >
                    <div className="flex items-center space-x-4">
                      <div
                        className={`p-2 rounded-lg ${
                          filter.type === "role"
                            ? "bg-green-100 text-green-700"
                            : filter.type === "alphabet"
                            ? "bg-purple-100 text-purple-700"
                            : "bg-blue-100 text-blue-700"
                        }`}
                      >
                        {filter.type === "role" ? (
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                          </svg>
                        ) : filter.type === "alphabet" ? (
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 20l4-16m6 16l4-16M6 9h14M4 15h14"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        )}
                      </div>
                      <div>
                        <div className="flex flex-wrap items-center gap-1">
                          <span className="text-sm font-medium text-gray-600">
                            {filter.label}:
                          </span>
                          <span
                            className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
                              filter.type === "role"
                                ? "bg-green-100 text-green-800"
                                : filter.type === "alphabet"
                                ? "bg-purple-100 text-purple-800"
                                : "bg-blue-100 text-blue-800"
                            }`}
                          >
                            {filter.value}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {filter.type === "role"
                            ? "Role Filter"
                            : filter.type === "alphabet"
                            ? "Alphabet Filter"
                            : "Search Filter"}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFilter(filter.key)}
                      className="mt-3 sm:mt-0 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 group self-start sm:self-auto"
                      title={`Remove ${filter.label} filter`}
                    >
                      <svg
                        className="w-4 h-4 group-hover:scale-110 transition-transform duration-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {/* Role Filter Buttons */}
        <div className="flex flex-wrap gap-4 mb-8">
          <button
            key="Current Office Bearers"
            onClick={() => handleRoleFilter("Current Office Bearers")}
            className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
              activeRole === "Current Office Bearers"
                ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg"
                : "bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            Current Office Bearers
          </button>
          <button
            key="Founder Member"
            onClick={() => handleRoleFilter("Founder Member")}
            className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
              activeRole === "Founder Member"
                ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg"
                : "bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            Founder Member
          </button>
          <button
            key="Past President"
            onClick={() => handleRoleFilter("Past President")}
            className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
              activeRole === "Past President"
                ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg"
                : "bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            Past President
          </button>
          {designations
            .filter(
              (role) => role && role.name && role.name !== "Past President"
            )
            .map((role) => (
              <button
                key={role._id}
                onClick={() => handleRoleFilter(role.name)}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  activeRole === role.name
                    ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg"
                    : "bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                {role.name}
              </button>
            ))}
        </div>

        {shouldShowAlphabetFilter() && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-green-50 to-blue-50 px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <svg
                  className="w-5 h-5 mr-2 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 20l4-16m6 16l4-16M6 9h14M4 15h14"
                  />
                </svg>
                Filter by First Name (A-Z)
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Available for {activeRole} only
              </p>
            </div>

            <div className="p-6">
              <div className="flex flex-wrap gap-2">
                {Array.from({ length: 26 }, (_, i) => {
                  const letter = String.fromCharCode(65 + i);
                  return (
                    <button
                      key={letter}
                      onClick={() => handleAlphabetFilter(letter)}
                      className={`h-10 w-10 rounded-lg font-semibold text-sm transition-all duration-300 ${
                        alphabetFilter === letter
                          ? "bg-blue-700 text-white shadow-lg transform scale-110"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900"
                      }`}
                    >
                      {letter}
                    </button>
                  );
                })}
              </div>
              {alphabetFilter && (
                <div className="mt-4 flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <span className="text-sm text-green-800 font-medium">
                    Showing members whose first name starts with "
                    {alphabetFilter}"
                  </span>
                  <button
                    onClick={() => handleAlphabetFilter("")}
                    className="text-green-600 hover:text-green-700 font-medium text-sm"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Member List */}
        <div
          ref={membersSectionRef}
          className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
        >
          <div className="p-8">
            {/* Items count and page summary */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 p-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl border border-gray-200">
              <div className="flex items-center space-x-2">
                <svg
                  className="w-5 h-5 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <span className="text-sm font-medium text-gray-700">
                  Showing{" "}
                  <span className="font-semibold text-blue-700">
                    {displayMembers.length}
                  </span>{" "}
                  member{displayMembers.length !== 1 ? "s" : ""} on page{" "}
                  <span className="font-semibold text-indigo-700">{page}</span>
                </span>
              </div>
            </div>

            {displayMembers.length > 0 ? (
              isFounderMemberView ? (
                <div className="space-y-2">
                  <div className="sticky top-0 z-10 border border-blue-700 rounded-lg p-3 mb-3 bg-white shadow-sm">
                    <div className="flex items-center mb-1">
                      <svg
                        className="w-4 h-4 text-blue-700 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3 .922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783 .57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81 .588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                        />
                      </svg>
                      <h3 className="text-lg font-bold text-blue-700">
                        Founder Members
                      </h3>
                    </div>
                    <p className="text-blue-700 text-xs">
                      Honoring the visionaries who established our organization
                    </p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden p-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      {displayMembers.map((member, idx) => {
                        const memberName = member.name || "N/A";
                        return (
                          <div
                            key={member.sno || idx}
                            className="flex items-center p-2 border border-gray-100 rounded-md hover:border-blue-200 transition-colors duration-200"
                          >
                            <div className="w-full">
                              <h4 className="text-sm font-bold text-black mb-1">
                                {memberName}
                              </h4>
                              <div className="flex items-center">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-800 text-white border border-blue-700">
                                  <svg
                                    className="w-3 h-3 mr-1"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3 .921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784 .57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81 .588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                  Founder Member
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                  {displayMembers.map((member, idx) => (
                    <div
                      key={idx}
                      className="transform hover:scale-105 transition-transform duration-300"
                    >
                      <SingleTeam
                        authorName={
                          activeRole === "Past President"
                            ? member.profileInfo?.displayName || "N/A"
                            : member.profileInfo &&
                              (member.profileInfo.firstName?.trim() ||
                                member.profileInfo.lastName?.trim())
                            ? `${member.profileInfo.firstName?.trim() || ""} ${
                                member.profileInfo.lastName?.trim() || ""
                              }`.trim()
                            : "N/A"
                        }
                        designation={
                          activeRole === "Current Office Bearers"
                            ? member?.memberDesignation?.name
                            : filters?.designation
                            ? member?.memberDesignation?.name
                            : filters?.memberRole
                            ? member?.memberRole
                            : filters?.pastPresident === "true"
                            ? "Past President"
                            : member?.memberDesignation?.name
                        }
                        membershipId={
                          activeRole === "Past President"
                            ? null
                            : member.membershipId || "N/A"
                        }
                        teamImage={
                          member?.profileInfo?.photo &&
                          member.profileInfo.photo.trim() !== ""
                            ? member.profileInfo.photo
                            : MemberImage
                        }
                        status={member.status}
                        id={member._id}
                        isTrue={true}
                      />
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className="text-center py-20">
                <svg
                  className="w-16 h-16 text-gray-400 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
                  />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Members Found
                </h3>
                <p className="text-gray-500">
                  Try adjusting your search criteria
                </p>
              </div>
            )}
          </div>
          {/* Pagination */}
          <div className="px-8 py-6 bg-gradient-to-r from-gray-50 to-blue-50 border-t-2 border-gray-100">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <button
                onClick={handlePrev}
                disabled={page === 1 || loading}
                className={`h-12 px-6 rounded-xl font-semibold transition-all duration-300 ${
                  page === 1 || loading
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : "bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                }`}
              >
                ← Previous Page
              </button>

              <span className="text-sm font-medium text-gray-700 bg-white px-4 py-2 rounded-lg border">
                Page {page}
              </span>

              <button
                onClick={handleNext}
                disabled={!effectiveHasMore || loading}
                className={`h-12 px-6 rounded-xl font-semibold transition-all duration-300 ${
                  !effectiveHasMore
                    ? "bg-gray-200 text-gray-500 !cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700"
                }`}
              >
                Next Page →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembersSection;
